import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import type { MagicLinkRequestResponse } from '@/api/types';
import { Sparkles, Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const requestMagicLink = useMutation({
    mutationFn: async (email: string) => {
      const response = await api.post<MagicLinkRequestResponse>(
        '/auth/magic-link/request',
        { email }
      );
      return response;
    },
    onSuccess: async (response) => {
      // In dev mode, token is returned directly
      if (response.data?.devToken) {
        await login(response.data.devToken);
        navigate('/welcome');
      } else {
        setSubmitted(true);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      requestMagicLink.mutate(email.trim());
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="card p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
          </div>

          {!submitted ? (
            <>
              <h1 className="text-2xl font-display font-bold text-center mb-2">
                Welcome to Identity Lab
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Enter your email to get started. No password needed!
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="label">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="input pl-10"
                      required
                      disabled={requestMagicLink.isPending}
                    />
                  </div>
                </div>

                {requestMagicLink.isError && (
                  <p className="text-sm text-red-600">
                    Something went wrong. Please try again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={requestMagicLink.isPending || !email.trim()}
                  className="btn-primary w-full py-3"
                >
                  {requestMagicLink.isPending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Continue with Email'
                  )}
                </button>
              </form>

              <p className="mt-6 text-xs text-center text-gray-500">
                We'll send you a magic link to sign in instantly.
                No password to remember!
              </p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-twins-light rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-twins" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Check your email!</h2>
              <p className="text-gray-600 mb-6">
                We sent a magic link to <strong>{email}</strong>
              </p>
              <p className="text-sm text-gray-500">
                Click the link in the email to sign in. The link expires in 15 minutes.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-6 text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Use a different email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
